import { useContext } from 'react';

import { AuthContext } from '../context/AuthContext';
import * as authService from '../services/authService';

/**
 * Custom hook to access authentication context and methods
 */
export const useAuth = () => {
  const { currentUser, loading, error, setError } = useContext(AuthContext);

  /**
   * Sign up a new user
   */
  const signUp = async (email: string, password: string, displayName: string) => {
    try {
      setError(null);
      return await authService.signUp(email, password, displayName);
    } catch (error: any) {
      setError(error.message);
      throw error;
    }
  };

  /**
   * Sign in an existing user
   */
  const signIn = async (email: string, password: string) => {
    try {
      setError(null);
      return await authService.signIn(email, password);
    } catch (error: any) {
      setError(error.message);
      throw error;
    }
  };

  /**
   * Sign out the current user
   */
  const signOut = async () => {
    try {
      setError(null);
      await authService.logOut();
    } catch (error: any) {
      setError(error.message);
      throw error;
    }
  };

  /**
   * Send a password reset email
   */
  const resetPassword = async (email: string) => {
    try {
      setError(null);
      await authService.resetPassword(email);
    } catch (error: any) {
      setError(error.message);
      throw error;
    }
  };

  /**
   * Update user profile
   */
  const updateProfile = async (displayName: string, photoURL?: string) => {
    if (!currentUser) {
      setError('No user is signed in');
      throw new Error('No user is signed in');
    }

    try {
      setError(null);
      await authService.updateUserProfile(currentUser, displayName, photoURL);
    } catch (error: any) {
      setError(error.message);
      throw error;
    }
  };

  /**
   * Update user email
   */
  const updateEmail = async (newEmail: string, password: string) => {
    if (!currentUser) {
      setError('No user is signed in');
      throw new Error('No user is signed in');
    }

    try {
      setError(null);
      await authService.updateUserEmail(currentUser, newEmail, password);
    } catch (error: any) {
      setError(error.message);
      throw error;
    }
  };

  /**
   * Update user password
   */
  const updatePassword = async (currentPassword: string, newPassword: string) => {
    if (!currentUser) {
      setError('No user is signed in');
      throw new Error('No user is signed in');
    }

    try {
      setError(null);
      await authService.updateUserPassword(currentUser, currentPassword, newPassword);
    } catch (error: any) {
      setError(error.message);
      throw error;
    }
  };

  return {
    currentUser,
    loading,
    error,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updateProfile,
    updateEmail,
    updatePassword,
    isAuthenticated: !!currentUser,
  };
};

export default useAuth;
